import React, { useState, useEffect } from 'react';

import { Colors, Text, TextProps } from '@revenuemarket/vds';
import { css } from '~libs/emotion';

type SwipeDirection = 'vertical' | 'horizontal';

interface SwipeTextProps {
  labels: string[];
  textStyle?: TextProps['textStyle'];
  color?: string;
  swipeInterval?: number;
  direction?: SwipeDirection;
}

type AnimationPhase = 'visible' | 'exit' | 'enter';

const SwipeText: React.FC<SwipeTextProps> = ({
  labels,
  textStyle = '13_400',
  color = Colors.white,
  swipeInterval = 4000,
  direction = 'vertical',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<AnimationPhase>('visible');

  useEffect(() => {
    if (labels.length <= 1) {
      setPhase('visible');
      setCurrentIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setPhase('exit');
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % labels.length);
        setPhase('enter');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setPhase('visible');
          });
        });
      }, 300);
    }, swipeInterval);

    return () => clearInterval(interval);
  }, [labels.length, swipeInterval]);

  const currentLabel = labels[currentIndex] || null;

  if (!currentLabel) return null;

  const getTransform = () => {
    if (direction === 'horizontal') {
      return `translateX(${phase === 'exit' ? '-100%' : phase === 'enter' ? '100%' : '0'})`;
    }
    return `translateY(${phase === 'exit' ? '100%' : phase === 'enter' ? '-100%' : '0'})`;
  };

  return (
    <div
      className={css`
        overflow: hidden;
      `}
    >
      <Text
        textStyle={textStyle}
        style={{ color }}
        className={css`
          display: block;
          transition: ${
            phase === 'enter' ? 'none' : 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out'
          };
          transform: ${getTransform()};
          opacity: ${phase === 'visible' ? 1 : 0};
        `}
      >
        {currentLabel}
      </Text>
    </div>
  );
};

export default SwipeText;
